import React, { useRef } from 'react'
import { PopupMenuItem, makeStyles } from '@paintscout/ui'
import type { MuiTheme as Theme, StyleClasses } from '@paintscout/ui'
import { PresEditorTooltip } from '../components'
import type { PresentationBlock, PresentationOptionV2, PresentationPageV2 } from 'paintscout'
import { usePresentation } from '../../context'
import { hasBlockOptions } from './hasBlockOptions'

import { findBlock, movePresentationNextBlock, uuid } from '@paintscout/util/builder'

import isEqual from 'lodash/isEqual'

import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward'
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward'
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline'
import EditOutlineIcon from '@material-ui/icons/EditOutlined'
import FileCopyOutlinedIcon from '@material-ui/icons/FileCopyOutlined'
import classnames from 'classnames'

const useStyles = makeStyles<Theme, BlockMenuProps>(
  (theme) => ({
    root: {},
    sectionMenu: {
      backgroundColor: theme.palette.grey[100],
      transition: `top .3s cubic-bezier(0.33, 1, 0.68, 1), border 0s ease-in .18s, right 0s ease-in .18s`,
      borderRadius: theme.borderRadius.md,
      boxShadow: theme.boxShadow[1],
      display: 'none',
      overflow: 'hidden',
      padding: 0,
      position: 'absolute',
      right: 0,
      top: 20,
      zIndex: 3
    },
    sectionFocusedMenu: {
      border: `2px solid ${theme.palette.presentation.sectionBorder.focus}`,
      borderBottom: 'none',
      borderBottomLeftRadius: 0,
      borderBottomRightRadius: 0,
      boxShadow: 'none',
      display: 'flex',
      top: -50,
      right: -2
    },
    deleteIcon: {
      '& svg': {
        color: 'red'
      },
      '&:hover': {
        '& svg': {
          fill: 'red'
        }
      }
    }
  }),
  { name: 'BlockMenu' }
)

export interface BlockMenuProps {
  classes?: StyleClasses<typeof useStyles>
  addPages: boolean
  focusedBlock: string
  handleCreateBlock: (newBlock: PresentationBlock, args: { sectionKey: string; index?: number }) => void
  handleDeleteBlock: (blockKey: string) => void
  handlePagesChange: (pages: PresentationPageV2[]) => void
  index: number
  /** The section this block lives in — the destination for duplicate. */
  sectionKey: string
  presentation: PresentationOptionV2
  block: PresentationBlock
  sectionOptionsOpen: boolean
  setFocusedBlock: (blockKey: string) => void
  setMenuAnchorEl: React.Dispatch<React.SetStateAction<HTMLElement>>
}

function BlockMenu(props: BlockMenuProps) {
  const classes = useStyles(props)
  const {
    addPages,
    focusedBlock,
    handleCreateBlock,
    handleDeleteBlock,
    handlePagesChange,
    index,
    sectionKey,
    presentation,
    block,
    sectionOptionsOpen,
    setFocusedBlock,
    setMenuAnchorEl
  } = props
  const optionsMenuRef = useRef<HTMLSpanElement>()
  const { useCart } = usePresentation()

  const isQuoteSection = ['quote', 'work-order'].includes(block.type)
  const showDuplicateSection = !isQuoteSection && block.type !== 'terms'
  // avoids opening an empty popup for block types with nothing to configure (#21821)
  const showOptions = hasBlockOptions({ block, useCart })

  return (
    <div
      className={classnames(classes.sectionMenu, {
        ['sectionHoverMenu']: !sectionOptionsOpen,
        [classes.sectionFocusedMenu]: focusedBlock === block.key && !sectionOptionsOpen
      })}
    >
      {showOptions && (
        <PresEditorTooltip title={`Block Options`}>
          <span ref={optionsMenuRef}>
            <PopupMenuItem
              icon={EditOutlineIcon}
              iconOnly
              onClick={(ev) =>
                handleOpenSectionOptions(ev, block.key, setFocusedBlock, setMenuAnchorEl, optionsMenuRef.current)
              }
            />
          </span>
        </PresEditorTooltip>
      )}
      {!addPages && (
        <>
          {showDuplicateSection && (
            <PresEditorTooltip title={`Duplicate Block`}>
              <PopupMenuItem
                icon={FileCopyOutlinedIcon}
                iconOnly
                onClick={() =>
                  handleDuplicateBlock({
                    block: block,
                    blockIndex: index,
                    sectionKey,
                    handleCreateBlock,
                    setFocusedBlock
                  })
                }
              />
            </PresEditorTooltip>
          )}
          <PresEditorTooltip title={`Move Block Down`}>
            <PopupMenuItem
              icon={ArrowDownwardIcon}
              iconOnly
              onClick={() => handleMoveBlock(presentation, 'down', block.key, handlePagesChange)}
            />
          </PresEditorTooltip>
          <PresEditorTooltip title={`Move Block Up`}>
            <PopupMenuItem
              icon={ArrowUpwardIcon}
              iconOnly
              onClick={() => handleMoveBlock(presentation, 'up', block.key, handlePagesChange)}
            />
          </PresEditorTooltip>
          <PresEditorTooltip title={`Delete Block`}>
            <PopupMenuItem
              icon={DeleteOutlineIcon}
              iconOnly
              onClick={() => handleDelete(block.key, setFocusedBlock, handleDeleteBlock)}
              classes={{ root: classes.deleteIcon }}
            />
          </PresEditorTooltip>
        </>
      )}
    </div>
  )
}

export default React.memo(BlockMenu, (prevProps, nextProps) => {
  if (prevProps.addPages !== nextProps.addPages) return false
  if (prevProps.sectionKey !== nextProps.sectionKey) return false
  if (prevProps.focusedBlock !== nextProps.focusedBlock) return false
  if (prevProps.index !== nextProps.index) return false
  if (prevProps.sectionOptionsOpen !== nextProps.sectionOptionsOpen) return false
  if (!isEqual(prevProps.block, nextProps.block)) return false
  if (!isEqual(prevProps.presentation, nextProps.presentation)) return false

  // functions shouldn't change
  // handleCreateBlock
  // handleDeleteBlock
  // handlePagesChange
  // setFocusedBlock
  // setMenuAnchorEl
  return true
})

/**
 * Moves a block one position up or down, crossing into the neighbouring section (and page) when it's
 * already at an edge — so the arrows walk the whole presentation rather than stopping at a boundary.
 *
 * The traversal is computed here; the mutation is delegated to movePresentationNextBlock, which is
 * immutable and key-addressed.
 */
function handleMoveBlock(
  presentation: PresentationOptionV2,
  direction: 'up' | 'down',
  blockKey: string,
  handlePagesChange: (pages: PresentationPageV2[]) => void
) {
  const location = findBlock(presentation, blockKey)

  if (!location) {
    return
  }

  const { section, blockIndex } = location
  const atStart = blockIndex === 0
  const atEnd = blockIndex === section.blocks.length - 1

  // a flat list of every section in document order, so "past the edge" means the adjacent section
  const allSections = presentation.pages.flatMap((page) => page.sections)
  const sectionIndex = allSections.findIndex((candidate) => candidate.key === section.key)

  let toSectionKey = section.key
  let toIndex = direction === 'up' ? blockIndex - 1 : blockIndex + 1

  if (direction === 'up' && atStart) {
    const previous = allSections[sectionIndex - 1]
    if (!previous) {
      return
    }
    toSectionKey = previous.key
    toIndex = previous.blocks.length
  }

  if (direction === 'down' && atEnd) {
    const next = allSections[sectionIndex + 1]
    if (!next) {
      return
    }
    toSectionKey = next.key
    toIndex = 0
  }

  const updated = movePresentationNextBlock({ presentation, blockKey, toSectionKey, toIndex })

  handlePagesChange(updated.pages)
}

function handleDuplicateBlock(args: {
  block: PresentationBlock
  blockIndex: number
  sectionKey: string
  handleCreateBlock: (newBlock: PresentationBlock, args: { sectionKey: string; index?: number }) => void
  setFocusedBlock: (blockKey: string) => void
}) {
  const { block, blockIndex, sectionKey, handleCreateBlock, setFocusedBlock } = args
  const newBlock = { ...block, key: uuid() }

  handleCreateBlock(newBlock, { sectionKey, index: blockIndex + 1 })
  setFocusedBlock(newBlock.key)
}

function handleDelete(blockKey: string, setFocusedBlock: any, handleDeleteBlock: (blockKey: string) => void) {
  setFocusedBlock('')
  handleDeleteBlock(blockKey)
}

const handleOpenSectionOptions = (
  ev: React.MouseEvent<any, MouseEvent>,
  blockKey: string,
  setFocusedBlock: any,
  setMenuAnchorEl: any,
  menuAnchorEl: any
) => {
  ev.stopPropagation()
  setFocusedBlock(blockKey)
  setMenuAnchorEl(menuAnchorEl)
}
